import { Fragment, useEffect, useState } from 'react'
import { Check, ChevronDown, ChevronRight } from 'lucide-react'

import type {
  AgentOrchestratorPlanStep,
  AgentOrchestratorStepResult,
  LiveSegment
} from '@axilon-apps/domains-ontology-ui-state'
import { ChatMarkdown } from '../ChatMarkdown'
import { cn } from '../../lib/utils'
import { agentLabel } from '../../lib/agentLabels'
import { stripAnswerMarkers } from './answerEnvelope'
import { deriveStepStatus, type StepStatus } from './stepStatus'

function displayCotText(text: string): string {
  return stripAnswerMarkers(text)
}

interface LiveTranscriptProps {
  plan: AgentOrchestratorPlanStep[]
  stepResults: Record<number, AgentOrchestratorStepResult>
  segmentsByStep: Record<number, LiveSegment[]>
  pendingByStep: Record<number, string>
  /**
   * Per-step orchestrator inter-step narration, keyed by the step the
   * orchestrator is about to dispatch. Streams in token-by-token before
   * each subagent fires and renders as a short prose block above the
   * matching step section.
   */
  interStepTextByStep: Record<number, string>
  /**
   * `true` once the orchestrator has finalized (or for any past-turn
   * render). When set, every step section auto-collapses so the chat
   * settles into a quiet "everything's done" state — the user can still
   * click any agent header to expand and inspect.
   */
  isComplete: boolean
  /**
   * `steps` (default): per-agent collapsible sections (orchestrator task plan).
   * `flat`: CoT only — used by ingest Ask (`ingest_query_agent_only`) where a
   * synthetic single-step plan would otherwise look like a task plan.
   */
  variant?: 'steps' | 'flat'
  /**
   * The outer reasoning line is already the collapse. When true, keep step
   * sections open after the turn completes and still render steps that have
   * no leftover CoT (plan description only — never tool names).
   */
  revealDetails?: boolean
}

/**
 * Per-step live transcript: at any moment only the latest progress note
 * (or the streaming pending text) is visible inline. Older committed
 * notes hide behind a "Show N earlier thoughts" expander. The whole
 * step section collapses under its agent header, with a thought count
 * shown in the collapsed state.
 *
 * Every running step (started but not yet completed, while the turn is
 * live) auto-expands so the user sees its narration without clicking, and
 * collapses once it completes. A parallel dispatch wave therefore shows
 * several expanded step sections streaming at once. User toggles stick
 * for the tab session.
 *
 * A step section renders as soon as its dispatch starts (`step_start`
 * upserts a partial result) — before the first narration delta arrives —
 * with a "Working…" placeholder body, so a parallel wave shows every
 * in-flight subagent rather than only the ones currently emitting text.
 * `text` steps are excluded from that early render: their prose arrives
 * as inter-step narration, not a step section. Past turns (and the
 * finalized render) fall back to content-only sections. Past-turn
 * rendering also flows through this component via
 * `turn.progressByStep` → `liveSegmentsByStep`.
 */
export function LiveTranscript({
  plan,
  stepResults,
  segmentsByStep,
  pendingByStep,
  interStepTextByStep,
  isComplete,
  variant = 'steps',
  revealDetails = false
}: LiveTranscriptProps) {
  const visibleSteps = plan
    .map((step) => ({
      step,
      segments: segmentsByStep[step.index] ?? [],
      pending: pendingByStep[step.index] ?? '',
      interStepText: interStepTextByStep[step.index] ?? ''
    }))
    .filter(({ step, segments, pending, interStepText }) => {
      const hasContent = segments.length > 0 || pending.length > 0 || interStepText.length > 0
      if (hasContent) return true
      if (step.intended_agent === 'text') return false
      if (!isComplete && stepResults[step.index] !== undefined) return true
      return isComplete && revealDetails
    })

  if (visibleSteps.length === 0) return null

  if (variant === 'flat') {
    return (
      <div className="flex flex-col gap-2">
        {visibleSteps.map(({ step, segments, pending, interStepText }) => {
          const status = deriveStepStatus(stepResults, step.index, isComplete)
          const isActive = status === 'running'
          return (
            <Fragment key={step.index}>
              {interStepText.length > 0 ? (
                <div className="chat-markdown sidebar-chat min-w-0 px-1 text-foreground">
                  <ChatMarkdown isStreaming={!isComplete}>{interStepText}</ChatMarkdown>
                </div>
              ) : null}
              <FlatCotBody segments={segments} pending={pending} isActive={isActive} />
            </Fragment>
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {visibleSteps.map(({ step, segments, pending, interStepText }) => {
        const status = deriveStepStatus(stepResults, step.index, isComplete)
        const isActive = status === 'running'
        return (
          <Fragment key={step.index}>
            {interStepText.length > 0 ? (
              <div className="chat-markdown sidebar-chat min-w-0 px-1 text-foreground">
                <ChatMarkdown isStreaming={!isComplete}>{interStepText}</ChatMarkdown>
              </div>
            ) : null}
            <StepSection
              step={step}
              segments={segments}
              pending={pending}
              isActive={isActive}
              isComplete={isComplete}
              status={status}
              keepOpen={revealDetails}
            />
          </Fragment>
        )
      })}
    </div>
  )
}

/** Flat CoT for single-agent Ask — no agent/task header chrome. */
function FlatCotBody({
  segments,
  pending,
  isActive
}: {
  segments: LiveSegment[]
  pending: string
  isActive: boolean
}) {
  const [olderOpen, setOlderOpen] = useState(false)
  const isStreaming = pending.length > 0
  const latest = isStreaming ? pending : (segments[segments.length - 1]?.text ?? '')
  const olderSegments = isStreaming ? segments : segments.slice(0, -1)
  const olderCount = olderSegments.length
  const latestKey = segments.length + (isStreaming ? 1 : 0)

  return (
    <div className="flex flex-col gap-1 text-xs leading-snug text-muted-foreground">
      {olderCount > 0 ? (
        <>
          <button
            type="button"
            onClick={() => setOlderOpen((v) => !v)}
            className="flex w-fit cursor-pointer items-center gap-1 text-10 text-muted-foreground/70 transition-colors hover:text-foreground/80"
            aria-expanded={olderOpen}
          >
            {olderOpen ? (
              <ChevronDown className="size-2.5" />
            ) : (
              <ChevronRight className="size-2.5" />
            )}
            <span>{olderOpen ? 'Hide earlier' : 'Show all'}</span>
          </button>
          <div
            className={cn(
              'grid overflow-hidden transition-all duration-300 ease-out',
              olderOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            )}
          >
            <div className="min-h-0 flex flex-col gap-1 border-l border-border/60 pl-3 text-muted-foreground/60">
              {olderSegments.map((seg, i) => (
                <div
                  key={i}
                  className="chat-markdown sidebar-chat min-w-0 break-words [&>*:first-of-type]:mt-0 [&>*:last-child]:mb-0"
                >
                  <ChatMarkdown>{displayCotText(seg.text)}</ChatMarkdown>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : null}

      {displayCotText(latest).length > 0 ? (
        <div
          key={latestKey}
          className="chat-markdown sidebar-chat min-w-0 break-words text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-500 ease-out [&>*:first-of-type]:mt-0 [&>*:last-child]:mb-0"
        >
          <ChatMarkdown isStreaming={isStreaming}>{displayCotText(latest)}</ChatMarkdown>
        </div>
      ) : isActive ? (
        <div className="animate-pulse text-11 text-muted-foreground/60 italic">Working…</div>
      ) : null}
    </div>
  )
}

interface StepSectionProps {
  step: AgentOrchestratorPlanStep
  segments: LiveSegment[]
  pending: string
  isActive: boolean
  isComplete: boolean
  status: StepStatus
  keepOpen: boolean
}

function StepSection({
  step,
  segments,
  pending,
  isActive,
  isComplete,
  status,
  keepOpen
}: StepSectionProps) {
  const [agentOpen, setAgentOpen] = useState(isActive || keepOpen)
  const [olderOpen, setOlderOpen] = useState(false)

  // Auto-toggle the section based on state transitions:
  //   - Run finished → collapse so the chat settles, unless the parent
  //     reasoning disclosure is already the collapse (`keepOpen`)
  //   - Step becomes active (newly-running) → expand so the user sees it
  //   - Step is no longer active (it completed) → collapse
  // User-driven toggles persist because the effect only runs when the
  // relevant deps change; static renders don't re-collapse.
  useEffect(() => {
    if (keepOpen) {
      setAgentOpen(true)
    } else if (isComplete) {
      setAgentOpen(false)
    } else if (isActive) {
      setAgentOpen(true)
    } else {
      setAgentOpen(false)
    }
  }, [isActive, isComplete, keepOpen])

  const isStreaming = pending.length > 0
  const latest = isStreaming ? pending : (segments[segments.length - 1]?.text ?? '')
  const olderSegments = isStreaming ? segments : segments.slice(0, -1)
  const olderCount = olderSegments.length

  // Key the latest <p> by the count of distinct messages that have
  // appeared in this slot. The count increments by one whenever a new
  // pending begins (new commit-or-stream cycle) but is stable while a
  // pending streams token-by-token. React re-mounts the element on key
  // change, which retriggers the `animate-in slide-in-from-bottom`
  // entrance so each new thought visibly slides upward into the slot
  // the previous one was occupying.
  const latestKey = segments.length + (isStreaming ? 1 : 0)

  const agentLabelText = agentLabel(step.intended_agent)

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setAgentOpen((v) => !v)}
        className="flex w-full cursor-pointer items-start gap-1 rounded-sm py-0.5 text-left text-11 text-muted-foreground transition-colors hover:text-foreground"
        aria-expanded={agentOpen}
      >
        {agentOpen ? (
          <ChevronDown className="mt-0.5 size-3 shrink-0" />
        ) : (
          <ChevronRight className="mt-0.5 size-3 shrink-0" />
        )}
        <span className="shrink-0 font-medium text-foreground/80 tracking-wide uppercase">
          {agentLabelText}
        </span>
        <span className="shrink-0 text-muted-foreground/60">·</span>
        <span className="min-w-0 flex-1 break-words text-muted-foreground/80">
          {step.description}
        </span>
        <StatusIcon status={status} />
      </button>

      <div
        className={cn(
          'grid overflow-hidden transition-all duration-300 ease-out',
          agentOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="min-h-0 ml-1.5 flex flex-col gap-1 border-l border-border/50 pt-0.5 pr-1 pb-1 pl-3 text-xs leading-snug text-muted-foreground">
          {olderCount > 0 ? (
            <>
              <button
                type="button"
                onClick={() => setOlderOpen((v) => !v)}
                className="flex w-fit cursor-pointer items-center gap-1 text-10 text-muted-foreground/70 transition-colors hover:text-foreground/80"
                aria-expanded={olderOpen}
              >
                {olderOpen ? (
                  <ChevronDown className="size-2.5" />
                ) : (
                  <ChevronRight className="size-2.5" />
                )}
                <span>{olderOpen ? 'Hide earlier' : 'Show all'}</span>
              </button>
              <div
                className={cn(
                  'grid overflow-hidden transition-all duration-300 ease-out',
                  olderOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                )}
              >
                <div className="min-h-0 flex flex-col gap-1 border-l border-border/60 pl-3 text-muted-foreground/60">
                  {olderSegments.map((seg, i) => {
                    const text = displayCotText(seg.text)
                    if (text.length === 0) return null
                    return (
                      <div
                        key={i}
                        className="chat-markdown sidebar-chat min-w-0 break-words [&>*:first-of-type]:mt-0 [&>*:last-child]:mb-0"
                      >
                        <ChatMarkdown>{text}</ChatMarkdown>
                      </div>
                    )
                  })}
                </div>
              </div>
            </>
          ) : null}

          {displayCotText(latest).length > 0 ? (
            <div
              key={latestKey}
              className="chat-markdown sidebar-chat min-w-0 break-words text-muted-foreground animate-in fade-in slide-in-from-bottom-1 duration-500 ease-out [&>*:first-of-type]:mt-0 [&>*:last-child]:mb-0"
            >
              <ChatMarkdown isStreaming={isStreaming}>{displayCotText(latest)}</ChatMarkdown>
            </div>
          ) : isActive ? (
            // Dispatched but no narration yet (model still in its first
            // tool-call phase). Placeholder keeps the section visibly alive
            // so silence reads as work, not a stall.
            <div className="animate-pulse text-11 text-muted-foreground/60 italic">Working…</div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function StatusIcon({ status }: { status: StepStatus }) {
  if (status === 'running') {
    return (
      <span
        className="mt-1.25 inline-block size-1.5 shrink-0 animate-pulse rounded-full bg-primary"
        aria-hidden="true"
      />
    )
  }
  if (status === 'done') {
    return <Check className="mt-0.5 size-3 shrink-0 text-st-run" aria-hidden="true" />
  }
  return (
    <span
      className="mt-1.25 inline-block size-1.5 shrink-0 rounded-full border border-muted-foreground/60"
      aria-hidden="true"
    />
  )
}
